import React, { useEffect, useRef, useState, useId } from "react";
import { VideoJS } from "./VideoJS/VideoJS.js";
import { SeekbarEvents } from "./SeekbarEvents/SeekbarEvents.js";
import regularFullscreen from "../../../images/regularFullscreen.svg";
import focusLeft from "../../../images/fullscreenFocusLeft.svg";
import focusRight from "../../../images/fullscreenFocusRight.svg";
import leftOnly from "../../../images/fullscreenLeftOnly.svg";
import rightOnly from "../../../images/fullscreenRightOnly.svg";
import "./ProctoringResultView.css";
import apiClient from "../../../../helpers/api-client.js";
import PlayArrowRoundedIcon from "../../Resources/play.svg?react";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import FullscreenRoundedIcon from "../../Resources/fullScreen.svg?react";
import ExitFullscreenIcon from "../../Resources/exitFullscreen.svg?react";
import DownloadRoundedIcon from "../../Resources/download.svg?react";
import SvgIcon from "@mui/material/SvgIcon";
import i18n from "../../../../i18n.js";
import videoUnavailableSvg from "./images/recording_unavailable.svg";
import DownloadRequestModal, { DownloadRequestStatusType } from "./DownloadRequestModal.js";
import {
	screencaptureDisplayStateAtom,
	webcamDisplayStateAtom,
	videoDisplayStateAtom,
	videoEndDateAtom,
} from "./store.js";
import { TimestampsArr, VideoEvent } from "../../../utilities/useTimestamps.js";
import RecordingEndedSvg from "./images/RecordingEnded.js";
import { useAtomValue } from "jotai";
import Action from "./images/threedot.svg?react";
import { CoreMenuItem, CoreMenuButton } from "@verity/components/core";
import { ListItemText } from "@mui/material";
import { VideoOverlay } from "./ProctoringResultView.js";
import { formatEventTimestamp } from "../../ProctoringResultsPage/ProctoringEventsTable";
import DetectionOverlaySettings, { GearIcon } from "./overlay/DetectionOverlaySettings.js";
import styles from "./ProctoringVideoPlayer.module.css";
import { VideoControlButton, VolumeControls, Seekbar } from "./VideoControls/VideoControls.js";

export type ControlOptions = {
	isPlaying: boolean;
	playbackSpeed: number;
	volume: number;
};

export type SessionNote = {
	id: number | string;
	time: number;
	content: string;
	date: Date;
};

export type VideoSettings = {
	controls: boolean;
	responsive: boolean;
	aspectRatio: string;
	fluid: boolean;
	userActions: {
		click: boolean;
	};
	controlBar: {
		playToggle: boolean;
		volumePanel: boolean;
		progressControl: boolean;
		remainingTimeDisplay: boolean;
	};
};

export type ProctoringVideoPlayerProperties = {
	toggleVolume?: () => void;
	isFullscreen: boolean;
	sessionId: string;
	showTimestamps: boolean;
	/**
	 * TODO move to store
	 */
	controlOptions: ControlOptions;
	/**
	 * TODO move to store
	 */
	webcamVidSources: Record<string, string>;
	/**
	 * TODO move to store
	 */
	screenVidSources: Record<string, string>;
	/**
	 * TODO move to store
	 */
	playback: {
		timer: number;
		lastTime: number;
		skip: boolean;
	};
	/**
	 * TODO move to store
	 */
	playbackDispatch: React.Dispatch<
		| {
				type: "play";
				speed: number;
		  }
		| {
				type: "pause";
		  }
		| {
				type: "skip";
				time: number;
		  }
	>;
	/**
	 * TODO move to store
	 */
	startDate: Date;
	/**
	 * TODO move to store
	 */
	duration: number | undefined;
	/**
	 * TODO move to store
	 */
	timeOffset: number;
	/**
	 *  TODO: move to this component
	 */
	setFullscreen: () => void;
	/**
	 *  TODO: move to this component
	 */
	setPlay: () => void;
	/**
	 *  TODO: move to this component
	 */
	setVolume: React.ChangeEventHandler<HTMLInputElement>;
	/**
	 *  TODO: move to this component
	 */
	setSpeed: (value: string) => void;
	/**
	 *  TODO: move to this component
	 */
	showVideoUnavailable: boolean;
	/**
	 *  TODO: move to this component
	 */
	showAudioThumbnail: boolean;
	/**
	 *  TODO: move to this component
	 */
	showingVideoTitles: boolean;
	/**
	 *  TODO: move to this component
	 */
	webcamVideoOptions: VideoSettings | null | undefined;
	/**
	 *  TODO: move to this component
	 */
	screencaptureVideoOptions: VideoSettings | null | undefined;
	/**
	 *  TODO: move to this component
	 */
	downloadRequestStatus: DownloadRequestStatusType;
	/**
	 *  TODO: move to this component
	 */
	setDownloadRequestStatus: (value: DownloadRequestStatusType) => void;
	/**
	 *  TODO: move to this component
	 */
	setShowEmailModal: (value: boolean) => void;
	/**
	 *  TODO: move to this component
	 */
	setUserEnteredEmail: (value: string) => void;
	/**
	 *  TODO: move to this component
	 */
	setUserEnteredName: (value: string) => void;
	/**
	 * Refactor out
	 */
	shouldHideCamVid: boolean;
	/**
	 * Refactor out
	 */
	modifyFSMode: (changeMode: string) => void;
	/**
	 * Refactor out
	 */
	stringExtend: string;
	/**
	 * Refactor out
	 */
	events: VideoEvent[];
	/**
	 * Refactor out
	 */
	screenTimestampsArr: TimestampsArr;
	/**
	 * Refactor out
	 */
	webcamTimestampsArr: TimestampsArr;
	/**
	 * Refactor out
	 */
	sessionNotes: SessionNote[];
	/**
	 * Refactor out
	 */
	visibleLiveNotes: SessionNote[];
	/**
	 * Refactor out
	 */
	shouldShowDownload: boolean;

	isWebcamAndScreencaptureDeleted?: boolean;
	deleteConfirmationModal?: React.JSX.Element;
	setIsDeleteConfirmationModalShown?(value: "recording" | "roomscan" | undefined): void;
	hasInstituteEditPermission?: boolean;
	sessionStatus?: string | undefined | null;
	webCamHasError: boolean;
	setWebCamHasError: (value: boolean) => void;
	screenCaptureHasError: boolean;
	setScreenCaptureHasError: (value: boolean) => void;
	selectedProctoringSessionUuid?: string;
	showMachineLearningOverlay?: boolean;
};

function ProctoringVideoPlayer({
	isFullscreen,
	showTimestamps,
	controlOptions,
	playback,
	playbackDispatch,
	webcamVidSources,
	screenVidSources,
	startDate,
	duration,
	timeOffset,
	setFullscreen,
	setPlay,
	setVolume,
	setSpeed,
	showVideoUnavailable,
	showAudioThumbnail,
	showingVideoTitles,
	webcamVideoOptions,
	screencaptureVideoOptions,
	downloadRequestStatus,
	setDownloadRequestStatus,
	setShowEmailModal,
	setUserEnteredEmail,
	setUserEnteredName,
	modifyFSMode,
	stringExtend,
	events,
	screenTimestampsArr,
	webcamTimestampsArr,
	sessionNotes,
	visibleLiveNotes,
	shouldShowDownload,
	toggleVolume,
	isWebcamAndScreencaptureDeleted,
	deleteConfirmationModal,
	setIsDeleteConfirmationModalShown,
	hasInstituteEditPermission,
	sessionStatus,
	webCamHasError,
	setWebCamHasError,
	screenCaptureHasError,
	setScreenCaptureHasError,
	selectedProctoringSessionUuid,
	showMachineLearningOverlay = false,
}: ProctoringVideoPlayerProperties) {
	const controlIconSx = {
		color: "white",
		fontSize: isFullscreen ? "32px" : "22px",
		stroke: "white",
		fill: "none",
	};

	const videoDisplayState = useAtomValue(videoDisplayStateAtom);
	const videoEndDate = useAtomValue(videoEndDateAtom);

	const [menuShowing, setMenuShowing] = useState(false);
	const [showVideoSpeed, setShowVideoSpeed] = useState(false);
	const controlContainerRef = useRef<HTMLDivElement>(null);
	const playerContainerRef = useRef<HTMLDivElement>(null);

	const [isVideoPlayerOverlayVisible, setIsVideoPlayerOverlayVisible] = useState(true);

	const haveAllRecordingsFinishedPlaying = duration !== undefined && playback.timer >= duration - 0.01;

	useEffect(() => {
		setIsVideoPlayerOverlayVisible(!controlOptions.isPlaying);
	}, [controlOptions.isPlaying]);

	useEffect(() => {
		setWebCamHasError(!!isWebcamAndScreencaptureDeleted);
		setScreenCaptureHasError(!!isWebcamAndScreencaptureDeleted);
	}, [isWebcamAndScreencaptureDeleted]);

	let videoOverlayType: string | undefined;
	if (!screenCaptureHasError && !webCamHasError) {
		videoOverlayType = "both";
	} else if (!screenCaptureHasError && webCamHasError) {
		videoOverlayType = "screencapture";
	} else if (screenCaptureHasError && !webCamHasError) {
		videoOverlayType = "webcam";
	}

	// Note - didn't use videoOverlayType because videoOverlayType would be "both" even if it is only screen recording or only webcam
	const hasScreenRecordSource = screenVidSources && Object.keys(screenVidSources).length > 0;
	const hasWebcamSource = webcamVidSources && Object.keys(webcamVidSources).length > 0;
	// Only show fullscreen buttons if there is both webcam and screen recording AND we are in fullscreen
	const hideFullScreenButtons = !(hasScreenRecordSource && hasWebcamSource && isFullscreen);

	const videoOverlay = (
		<VideoOverlay
			useVideoEndedOverlay={haveAllRecordingsFinishedPlaying}
			isOverlayVisible={isVideoPlayerOverlayVisible}
			setPlay={setPlay}
		/>
	);

	const settingsButtonRef = useRef<HTMLButtonElement>(null);
	const [settingsPopoverOpen, setSettingsPopoverOpen] = useState(false);
	const settingsPopoverId = useId();

	useEffect(() => {
		const handleResize = () => {
			setSettingsPopoverOpen(false);
		};

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [setSettingsPopoverOpen]);

	return (
		<div
			className={!isFullscreen ? "left_side" : `left_side_fs`}
			style={showTimestamps ? { width: "fit-content" } : {}}
		>
			<div
				className={!isFullscreen ? "fullscreen-menu-holder" : `fullscreen-menu-holder_fs`}
				id="fullscreenMenuHolder"
				role="dialog"
				style={{ display: hideFullScreenButtons ? "none" : undefined }}
			>
				<button
					className="fullscreen_buttons hide"
					id="Centered_fs"
					onClick={(_e) => modifyFSMode("_fs")}
					aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.centerMode")}
				>
					<img id="Centered_fs_Button_Img" src={regularFullscreen} alt="" role="presentation" />
				</button>
				<button
					className="fullscreen_buttons"
					id="Left_and_Minimized_fs"
					onClick={(_e) => modifyFSMode("_fs_lm")}
					aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.screenWebcam")}
				>
					<img id="Left_and_Minimized_fs_Button_Img" src={focusLeft} alt="" role="presentation" />
				</button>
				<button
					className="fullscreen_buttons"
					id="Right_and_Minimized_fs"
					onClick={(_e) => modifyFSMode("_fs_rm")}
					aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.minimizedWebcam")}
				>
					<img id="Right_and_Minimized_fs_Button_Img" src={focusRight} alt="" role="presentation" />
				</button>
				<button
					className="fullscreen_buttons"
					id="Left_fs"
					onClick={(_e) => modifyFSMode("_fs_l")}
					aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.screenCaptureOnly")}
				>
					<img id="Left_fs_Button_Img" src={leftOnly} alt="" role="presentation" />
				</button>
				<button
					className="fullscreen_buttons"
					id="Right_fs"
					onClick={(_e) => modifyFSMode("_fs_r")}
					aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.webcamOnly")}
				>
					<img id="Right_fs_Button_Img" src={rightOnly} alt="" role="presentation" />
				</button>
			</div>
			<div
				className={
					`${videoOverlayType === "both" ? "overlay_container " : " "}` +
					(!isFullscreen ? "player_container" : `player_container_fs`)
				}
				ref={playerContainerRef}
			>
				{
					// only show the overlay on the available recording. If both videos are available, show the overlay covering both videos.
					videoOverlayType === "both" && sessionStatus !== "incomplete" && videoOverlay
				}

				{videoDisplayState === "ended" && (
					<div style={{ height: "100%" }}>
						<RecordingEndedSvg
							style={{
								height: "100%",
								width: "100%",
							}}
							endDate={videoEndDate ?? new Date(startDate.getTime() + (duration ?? 0))}
						/>
					</div>
				)}
				{hasScreenRecordSource && (
					<div
						className={
							`${videoOverlayType === "screencapture" ? "overlay_container " : " "}` +
							(!isFullscreen ? "screencapture_container" : `screencapture_container${stringExtend}`)
						}
						id="screencapture_container"
						style={{
							cursor: screenCaptureHasError ? undefined : "pointer",
							display: videoDisplayState === "ended" ? "none" : "",
						}}
						onClick={() => {
							if (videoOverlayType === "screencapture") setPlay();
						}}
					>
						{videoOverlayType === "screencapture" && sessionStatus !== "incomplete" && videoOverlay}
						<VideoJS
							events={events}
							debugging_string={"screencapture"}
							dispatch={playbackDispatch}
							options={screencaptureVideoOptions}
							controls={controlOptions}
							startDate={startDate}
							arrStamps={screenTimestampsArr}
							sources={screenVidSources}
							videoTimer={playback}
							displayStateAtom={screencaptureDisplayStateAtom}
							toggledTimestamps={showTimestamps}
							isFullscreen={isFullscreen}
							stringExtend={stringExtend}
							showingVideoTitles={showingVideoTitles}
							isDeleted={isWebcamAndScreencaptureDeleted}
							isIncomplete={sessionStatus === "incomplete"}
							videoHasError={screenCaptureHasError}
							setVideoHasError={setScreenCaptureHasError}
						/>
					</div>
				)}

				{hasWebcamSource && (
					<div
						className={
							`${videoOverlayType === "webcam" ? "overlay_container " : " "}` +
							(!isFullscreen ? "webcam_container" : `webcam_container${stringExtend}`)
						}
						id="webcam_container"
						style={{
							cursor: webCamHasError ? undefined : "pointer",
							display: videoDisplayState === "ended" ? "none" : "",
						}}
						onClick={() => {
							if (videoOverlayType === "webcam") setPlay();
						}}
					>
						{videoOverlayType === "webcam" && sessionStatus !== "incomplete" && videoOverlay}
						<VideoJS
							events={events}
							debugging_string={"webcam"}
							dispatch={playbackDispatch}
							options={webcamVideoOptions}
							controls={controlOptions}
							startDate={startDate}
							arrStamps={webcamTimestampsArr}
							sources={webcamVidSources}
							videoTimer={playback}
							displayStateAtom={webcamDisplayStateAtom}
							toggledTimestamps={showTimestamps}
							isFullscreen={isFullscreen}
							stringExtend={stringExtend}
							showingVideoTitles={showingVideoTitles}
							showAudioThumbnail={showAudioThumbnail}
							isDeleted={isWebcamAndScreencaptureDeleted}
							isIncomplete={sessionStatus === "incomplete"}
							videoHasError={webCamHasError}
							setVideoHasError={setWebCamHasError}
							selectedProctoringSessionUuid={selectedProctoringSessionUuid}
							showMachineLearningOverlay={showMachineLearningOverlay}
						/>
					</div>
				)}

				{videoDisplayState !== "ended" && showVideoUnavailable && (
					<div
						style={{
							height: "100%",
							width: "100%",
							justifyContent: "center",
							alignItems: "center",
							display: "flex",
						}}
					>
						<img style={{ maxWidth: "100%", height: "45vh" }} src={videoUnavailableSvg} alt="Video is unavailable" />
					</div>
				)}
			</div>
			{(screenVidSources || webcamVidSources) && (
				<div className={!isFullscreen ? "control_container" : `control_container_fs`} ref={controlContainerRef}>
					<VideoControlButton
						className={!isFullscreen ? "play_button" : `play_button_fs`}
						aria-label={
							controlOptions.isPlaying
								? i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.pauseVideoLabel")
								: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.playVideoLabel")
						}
						onClick={() => {
							// only enable playing if at least one video is available
							if (videoOverlayType) setPlay();
						}}
					>
						<SvgIcon
							sx={controlIconSx}
							component={controlOptions.isPlaying ? PauseRoundedIcon : PlayArrowRoundedIcon}
						/>
					</VideoControlButton>
					<VolumeControls
						volume={controlOptions.volume}
						showVolumeInput={!isWebcamAndScreencaptureDeleted}
						inFullscreen={isFullscreen}
						onVolumeButtonClick={toggleVolume}
						onVolumeInputChange={setVolume}
					/>
					<div className={!isFullscreen ? "video_time_wrap" : `video_time_wrap_fs`}>
						<span className={!isFullscreen ? "video_time" : `video_time_fs`}>
							{(webCamHasError && screenCaptureHasError) || sessionStatus === "incomplete"
								? "0:00/0:00"
								: `${formatEventTimestamp(playback.timer - timeOffset, (duration ?? 0) - timeOffset)}/${formatEventTimestamp((duration ?? 0) - timeOffset, (duration ?? 0) - timeOffset)}`}
						</span>
					</div>
					<Seekbar
						inFullscreen={isFullscreen}
						curSeekbarValue={playback.timer}
						minSeekbarValue={timeOffset}
						maxSeekbarValue={duration}
						onSeekbarChange={(e) => {
							playbackDispatch({
								type: "skip",
								time: e.target.valueAsNumber,
							});
						}}
						timeOffset={timeOffset}
					>
						<SeekbarEvents
							events={events}
							sessionNotes={sessionNotes}
							liveNotes={visibleLiveNotes}
							dispatch={playbackDispatch}
							duration={duration}
							globaltime={playback.timer / 1000}
							skip={playback.skip}
							offsetGetter={timeOffset}
							isFullscreen={isFullscreen}
						/>
					</Seekbar>

					<div className={"video_player_icons_wrapper"}>
						<CoreMenuButton
							className={!isFullscreen ? "video-speed-button" : `video-speed-button_fs`}
							menuShowing={showVideoSpeed}
							setMenuShowing={setShowVideoSpeed}
							style={{ backgroundColor: "#00000000" }}
							aria-label={i18n.t(
								"result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.playbackSpeed",
								{ speed: controlOptions.playbackSpeed },
							)}
							icon={<div style={{ color: "white" }}>{controlOptions.playbackSpeed}x</div>}
							slotProps={{
								menu: {
									transformOrigin: {
										vertical: "bottom",
										horizontal: "center",
									},
									anchorOrigin: {
										vertical: "top",
										horizontal: "center",
									},
									container: controlContainerRef.current,
								},
							}}
						>
							{[0.25, 0.5, 1, 1.25, 1.5, 1.75, 2, 4].map((x) => {
								return (
									<CoreMenuItem
										key={x}
										onClick={(_e) => {
											setSpeed(x.toString());
											setShowVideoSpeed(false);
										}}
									>
										<ListItemText primary={`${x}x`} />
									</CoreMenuItem>
								);
							})}
						</CoreMenuButton>
						{shouldShowDownload && !(webCamHasError && screenCaptureHasError) && !isFullscreen && (
							<VideoControlButton
								className="download_button"
								aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.downloadVideo")}
								onClick={async () => {
									setShowEmailModal(true);
									const accountUserId = await apiClient.user.getUserId();
									const accountUserInfo = await apiClient.user.getUserInfo(accountUserId);
									setUserEnteredEmail(accountUserInfo?.userInfo.email ? accountUserInfo?.userInfo.email : "");
									setUserEnteredName(accountUserInfo?.userInfo.email ? accountUserInfo?.userInfo.full_name : "");
								}}
							>
								<SvgIcon sx={controlIconSx} component={DownloadRoundedIcon} />
							</VideoControlButton>
						)}
						{!isFullscreen && (
							<DownloadRequestModal status={downloadRequestStatus} onClose={() => setDownloadRequestStatus("unsent")} />
						)}
						<div className={!isFullscreen ? "fullscreen_button" : `fullscreen_button_fs`}>
							<VideoControlButton
								className={!isFullscreen ? "actual_fullscreen_button" : `actual_fullscreen_button_fs`}
								onClick={() => setFullscreen()}
								aria-label={
									isFullscreen
										? i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.exitFullscreen")
										: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.enterFullscreen")
								}
							>
								<SvgIcon sx={controlIconSx} component={isFullscreen ? ExitFullscreenIcon : FullscreenRoundedIcon} />
							</VideoControlButton>
						</div>

						{(Object.entries(screenVidSources).length > 0 || Object.entries(webcamVidSources).length > 0) &&
							!isWebcamAndScreencaptureDeleted &&
							!isFullscreen &&
							hasInstituteEditPermission &&
							sessionStatus !== "incomplete" && (
								<CoreMenuButton
									aria-label={i18n.t(
										"result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.moreOptions",
									)}
									className="download_button"
									menuShowing={menuShowing}
									setMenuShowing={setMenuShowing}
									slotProps={{
										menu: {
											transformOrigin: {
												vertical: "bottom",
												horizontal: "center",
											},
											anchorOrigin: {
												vertical: "top",
												horizontal: "center",
											},
										},
									}}
									style={{ backgroundColor: "#17171D" }}
									icon={<SvgIcon sx={controlIconSx} component={Action}></SvgIcon>}
								>
									<CoreMenuItem
										onClick={() => {
											setIsDeleteConfirmationModalShown?.("recording");
											setMenuShowing(false);
										}}
									>
										<ListItemText primary={"Delete Recording"} />
									</CoreMenuItem>
								</CoreMenuButton>
							)}

						{/* Settings button */}
						{showMachineLearningOverlay && (
							<VideoControlButton
								className={styles.settingsButton}
								onClick={() => setSettingsPopoverOpen(true)}
								aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.settings")}
								aria-haspopup
								aria-controls={settingsPopoverId}
								ref={settingsButtonRef}
							>
								<GearIcon className={styles.settingsIcon} />
							</VideoControlButton>
						)}
					</div>
				</div>
			)}

			{/* Settings popover */}
			<DetectionOverlaySettings
				accessibility={{
					id: settingsPopoverId,
				}}
				refs={{
					anchor: settingsButtonRef,
					container: playerContainerRef,
				}}
				state={{
					open: settingsPopoverOpen,
				}}
				hooks={{
					onClose: () => setSettingsPopoverOpen(false),
				}}
			/>

			{deleteConfirmationModal}
		</div>
	);
}

export default ProctoringVideoPlayer;
